import {
  column,
  Css,
  dateColumn,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  RowStyles,
  simpleHeader,
} from "@homebound/beam";
import { useMemo } from "react";
import { dateCell, markupCell, Price, priceCell } from "src/components";
import { InvoiceStatus } from "src/generated/graphqlTypes";
import { mapToProjectStagesFilter, SingleStageFilter } from "src/hooks/useProjectStageSingleFilter";
import {
  HomeownerContractChangeOrderSummaryFragment,
  HomeownerContractSummaries_InvoiceFragment,
  HomeownerContractSummaries_ProjectItemAllocationFragment,
  HomeownerContractSummariesQuery,
  HomeownerContractSummaryFragment,
  HomeownerContractSummaryWithChangeOrdersFragment,
  useHomeownerContractSummariesQuery,
} from "src/routes/projects/homeowner-contracts/components/HomeownerContractsTable.generated";
import { createBudgetUrl, createHomeownerContractChangeOrderUrl, createHomeownerContractsUrl } from "src/RouteUrls";
import { formatCentsToPrice, queryResult } from "src/utils";

type HomeownerContractsTableProps = { projectId: string; filter: SingleStageFilter; showSharedCosts: boolean };

export function HomeownerContractsTable({ projectId, filter, showSharedCosts }: HomeownerContractsTableProps) {
  const query = useHomeownerContractSummariesQuery({
    variables: { projectId, stagesFilter: mapToProjectStagesFilter(filter), showSharedCosts },
    // We're using cache-and-network to re-fetch contracts after adding one via the Specs & Selections workflow
    nextFetchPolicy: "cache-and-network",
  });
  return queryResult(query, ({ project }) => <HomeownerContractsTableView project={project} />);
}

function HomeownerContractsTableView({ project }: { project: HomeownerContractSummariesQuery["project"] }) {
  const rows = useMemo(() => {
    const contracts = project.stages.flatMap((s) => s.homeownerContracts);
    const allocations = project.projectItemAllocations ?? [];
    const invoices = project.stages.flatMap((s) => s.invoices);
    return createRows(contracts, allocations, sumContracts(contracts, allocations, invoices));
  }, [project]);

  return (
    <GridTable
      columns={createColumns()}
      rows={rows}
      sorting={{ on: "client" }}
      rowStyles={createRowStyles(project.id)}
      style={{ rowHeight: "fixed", bordered: true, allWhite: true }}
      stickyHeader
      fallbackMessage="There are no signed contracts for the selected stage(s)."
    />
  );
}

type HeaderRow = { kind: "header" };
type TotalsRow = {
  kind: "totals";
  data: {
    invoicedInCents: number;
    uninvoicedInCents: number;
    amountPaidInCents: number;
    balanceInCents: number;
    budgetChangeInCents: number;
    priceChangeInCents: number;
    accruedInvoicedInCents: number;
    accruedAmountPaidInCents: number;
  };
};
type SharedCostTotalRow = {
  kind: "sharedCost";
  data: Pick<TotalsRow["data"], "budgetChangeInCents" | "priceChangeInCents">;
};
type ContractRow = { kind: "contract"; id: string; data: HomeownerContractSummaryFragment };
type ChangeOrderRow = {
  kind: "changeOrder";
  id: string;
  data: { changeOrder: HomeownerContractChangeOrderSummaryFragment; stageName: string };
};
type Row = TotalsRow | HeaderRow | ContractRow | ChangeOrderRow | SharedCostTotalRow;

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      totals: "Totals",
      header: "Contract",
      contract: ({ name }) => name,
      changeOrder: ({ changeOrder }) => ({
        content: `CO #${changeOrder.identifier}`,
        // Coerce string-sort back to numeric sort (so "10" comes after "9" instead of after "1")
        sortValue: Number(changeOrder.identifier),
      }),
      // Position before the contract rows
      sharedCost: {
        content: "Shared Costs",
        sortValue: 0,
        tooltip: "Budget and Price allocations coming from parent projects",
      },
      mw: "130px",
    }),
    column<Row>({
      totals: emptyCell,
      header: "Stage",
      contract: ({ projectStage }) => projectStage.stage.name,
      changeOrder: ({ stageName }) => stageName,
      sharedCost: emptyCell,
      w: "100px",
    }),
    dateColumn<Row>({
      totals: emptyCell,
      header: "Execution Date",
      contract: ({ executionDate }) => dateCell(executionDate),
      changeOrder: ({ changeOrder }) => dateCell(changeOrder.executionDate),
      sharedCost: emptyCell,
      w: "140px",
    }),
    numericColumn<Row>({
      totals: ({ budgetChangeInCents }) => priceCell({ valueInCents: budgetChangeInCents }),
      header: () => "Budgeted Cost",
      contract: ({ budgetChangeInCents }) => priceCell({ valueInCents: budgetChangeInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.budgetChangeInCents }),
      sharedCost: ({ budgetChangeInCents }) => priceCell({ valueInCents: budgetChangeInCents }),
      w: "130px",
    }),
    numericColumn<Row>({
      totals: ({ budgetChangeInCents, priceChangeInCents }) => markupCell(budgetChangeInCents, priceChangeInCents),
      header: () => "Markup",
      contract: ({ budgetChangeInCents, priceChangeInCents }) => markupCell(budgetChangeInCents, priceChangeInCents),
      changeOrder: ({ changeOrder: { budgetChangeInCents, priceChangeInCents } }) =>
        markupCell(budgetChangeInCents, priceChangeInCents),
      sharedCost: ({ budgetChangeInCents, priceChangeInCents }) => markupCell(budgetChangeInCents, priceChangeInCents),
      w: "200px",
    }),
    numericColumn<Row>({
      totals: ({ priceChangeInCents }) => priceCell({ valueInCents: priceChangeInCents }),
      header: () => "Contract Price",
      contract: ({ priceChangeInCents }) => priceCell({ valueInCents: priceChangeInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.priceChangeInCents }),
      sharedCost: ({ priceChangeInCents }) => priceCell({ valueInCents: priceChangeInCents }),
      w: "140px",
    }),
    numericColumn<Row>({
      totals: ({ invoicedInCents, accruedInvoicedInCents }) => ({
        content: <Price valueInCents={invoicedInCents} />,
        tooltip:
          accruedInvoicedInCents !== 0
            ? `Total invoiced, including ${formatCentsToPrice(accruedInvoicedInCents)} on Percentage of Completion/Soft Costs invoices`
            : "Total invoiced",
      }),
      header: () => "Invoiced",
      contract: ({ invoicedInCents }) => priceCell({ valueInCents: invoicedInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.invoicedInCents }),
      sharedCost: emptyCell,
      w: "120px",
    }),
    numericColumn<Row>({
      totals: ({ uninvoicedInCents, accruedInvoicedInCents }) => ({
        content: <Price valueInCents={uninvoicedInCents} />,
        tooltip:
          accruedInvoicedInCents !== 0
            ? `Remaining on contract, reduced by ${formatCentsToPrice(accruedInvoicedInCents)} on Percentage of Completion/Soft Costs invoices`
            : "Remaining on contract",
      }),
      header: () => "Remaining on Contract",
      contract: ({ uninvoicedInCents }) => priceCell({ valueInCents: uninvoicedInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.uninvoicedInCents }),
      sharedCost: emptyCell,
      w: "180px",
    }),
    numericColumn<Row>({
      totals: ({ amountPaidInCents, accruedAmountPaidInCents }) => {
        return {
          content: <Price valueInCents={amountPaidInCents} />,
          tooltip:
            accruedAmountPaidInCents !== 0
              ? `Total received, including ${formatCentsToPrice(accruedAmountPaidInCents)} on Percentage of Completion/Soft Costs invoices`
              : "Total received to date",
        };
      },
      header: () => "Received to Date",
      contract: ({ amountPaidInCents }) => priceCell({ valueInCents: amountPaidInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.amountPaidInCents }),
      sharedCost: emptyCell,
      w: "150px",
    }),
    numericColumn<Row>({
      totals: ({ balanceInCents, accruedAmountPaidInCents, accruedInvoicedInCents }) => {
        const accruedBalanceInCents = accruedInvoicedInCents - accruedAmountPaidInCents;
        return {
          content: <Price valueInCents={balanceInCents} />,
          tooltip:
            accruedBalanceInCents !== 0
              ? `Balance outstanding, including ${formatCentsToPrice(accruedBalanceInCents)} on Percentage of Completion/Soft Costs invoices`
              : "Balance outstanding",
        };
      },
      header: () => "Balance Outstanding",
      contract: ({ balanceInCents }) => priceCell({ valueInCents: balanceInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.balanceInCents }),
      sharedCost: emptyCell,
      w: "170px",
    }),
  ];
}

function createRows(
  contracts: HomeownerContractSummaryWithChangeOrdersFragment[],
  allocations: HomeownerContractSummaries_ProjectItemAllocationFragment[],
  totals: HomeownerContractTotals["data"],
): GridDataRow<Row>[] {
  return [
    simpleHeader,
    { kind: "totals", id: "totals", data: totals },
    ...createSharedCostRows(allocations),
    ...contracts.map((contract) => {
      return {
        kind: "contract" as const,
        id: contract.id,
        data: contract,
        children: contract.changeOrders.map((co) => ({
          kind: "changeOrder" as const,
          id: co.id,
          data: { changeOrder: co, stageName: contract.projectStage.stage.name },
        })),
      };
    }),
  ];
}

function createRowStyles(projectId: string): RowStyles<Row> {
  return {
    header: {},
    contract: { rowLink: ({ id }) => createHomeownerContractsUrl(projectId, id) },
    changeOrder: { rowLink: ({ data }) => createHomeownerContractChangeOrderUrl(projectId, data.changeOrder.id) },
    sharedCost: { cellCss: Css.xsSb.$, rowLink: () => createBudgetUrl(projectId) },
  };
}

function createSharedCostRows(allocations: HomeownerContractSummaries_ProjectItemAllocationFragment[]) {
  if (allocations.isEmpty) return [];
  return [{ kind: "sharedCost" as const, id: "sharedCost", data: sumSharedCostTotals(allocations) }];
}

type HomeownerContractTotals = Omit<TotalsRow, "kind">;

function sumContracts(
  contracts: HomeownerContractSummaryWithChangeOrdersFragment[],
  allocations: HomeownerContractSummaries_ProjectItemAllocationFragment[],
  invoices: HomeownerContractSummaries_InvoiceFragment[],
): HomeownerContractTotals["data"] {
  const flattenedContracts = contracts.flatMap(({ changeOrders, ...contract }) => [contract, ...changeOrders]);
  const totals = flattenedContracts.reduce(
    (acc, contract) => ({
      budgetChangeInCents: acc.budgetChangeInCents + (contract.budgetChangeInCents || 0),
      priceChangeInCents: acc.priceChangeInCents + (contract.priceChangeInCents || 0),
      invoicedInCents: acc.invoicedInCents + (contract.invoicedInCents || 0),
      uninvoicedInCents: acc.uninvoicedInCents + (contract.uninvoicedInCents || 0),
      amountPaidInCents: acc.amountPaidInCents + (contract.amountPaidInCents || 0),
      balanceInCents: acc.balanceInCents + (contract.balanceInCents || 0),
      accruedInvoicedInCents: 0,
      accruedAmountPaidInCents: 0,
    }),
    {
      budgetChangeInCents: 0,
      priceChangeInCents: 0,
      invoicedInCents: 0,
      amountPaidInCents: 0,
      uninvoicedInCents: 0,
      balanceInCents: 0,
      accruedInvoicedInCents: 0,
      accruedAmountPaidInCents: 0,
    },
  );

  if (allocations.nonEmpty) {
    const allocationsTotals = sumSharedCostTotals(allocations);
    totals.priceChangeInCents += allocationsTotals.priceChangeInCents;
    totals.budgetChangeInCents += allocationsTotals.budgetChangeInCents;
  }

  const maybeAccruedInvoicesTotals = sumAccruedInvoices(invoices);
  totals.accruedInvoicedInCents = maybeAccruedInvoicesTotals.invoicedInCents;
  totals.accruedAmountPaidInCents = maybeAccruedInvoicesTotals.amountPaidInCents;
  totals.invoicedInCents += maybeAccruedInvoicesTotals.invoicedInCents;
  totals.amountPaidInCents += maybeAccruedInvoicesTotals.amountPaidInCents;
  totals.balanceInCents += maybeAccruedInvoicesTotals.balanceInCents;
  // Accrued invoiced amounts bill against the project budget, so they reduce what's still remaining on the contract
  totals.uninvoicedInCents -= maybeAccruedInvoicesTotals.invoicedInCents;

  return totals;
}

function sumSharedCostTotals(
  allocations: HomeownerContractSummaries_ProjectItemAllocationFragment[],
): SharedCostTotalRow["data"] {
  const hclias = allocations.flatMap((pia) => pia.homeownerContractLineItemAllocations);
  return {
    priceChangeInCents: hclias.sum((hclia) => hclia.priceInCents),
    budgetChangeInCents: hclias.sum((hclia) => hclia.costInCents),
  };
}

// Accrued Invoices are not accounted for in the homeowner contract invoiced amount as they are based on cost accruals
// As they are still invoiced against project budget, we need to add them to the total,
// otherwise there always will be an open balance in the project
function sumAccruedInvoices(invoices: HomeownerContractSummaries_InvoiceFragment[]) {
  const invoicedInCents = invoices.sum((i) => i.accruedAmountInCents);
  const amountPaidInCents = invoices
    .filter((i) => i.status.code === InvoiceStatus.Paid)
    .sum((i) => i.accruedAmountInCents);

  return { invoicedInCents, amountPaidInCents, balanceInCents: invoicedInCents - amountPaidInCents };
}
